import React, { FC, useMemo } from "react";
import { StyleProp, TextStyle, Pressable, View, ViewStyle } from "react-native";
import { Checkbox, Modal, Text } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { useStaticInfo } from "../../../../hooks/useStaticInfo";
import { style as modalStyle } from "./SelectModal.style";

interface SelectModalStyle {
  readonly modalContent: StyleProp<ViewStyle>;
  readonly optionText: StyleProp<TextStyle>;
}

interface Option {
  readonly label: string;
  readonly value: string;
}

interface SelectModalProps {
  readonly title?: string;
  readonly value: string | undefined;
  readonly options: Option[];
  readonly style?: Partial<SelectModalStyle>;
  readonly modalVisible: boolean;
  readonly testID?: string;
  readonly portalHostName?: string;
  readonly onChange?: (value: string) => void;
  readonly onClose?: () => void;
}

const SelectModal: FC<SelectModalProps> = ({
  modalVisible,
  options,
  style: customStyle,
  testID,
  title,
  value,
  portalHostName,
  onChange = () => void 0,
  onClose = () => void 0,
}) => {
  const style = useMemo(() => modalStyle(), []);
  const { tradename } = useStaticInfo();

  return (
    <Modal
      portalHostName={portalHostName}
      testID={testID}
      visible={modalVisible}
      scroll
      showCloseButton
      onClose={onClose}
    >
      <View style={[style.modalContent, customStyle?.modalContent]}>
        {title && (
          <Text level={3} style={style.modalTitle} variant="heading">
            {title}
          </Text>
        )}
        <View accessibilityRole="radiogroup" style={style.container} testID="options-radiogroup">
          {options.map(({ label, value: optionValue }) => {
            const checked = value === optionValue;

            return (
              <Pressable
                key={optionValue}
                accessibilityLabel={label}
                accessibilityRole="radio"
                accessibilityState={{ checked, disabled: checked }}
                disabled={checked}
                testID={optionValue}
                style={[
                  style.wrapper,
                  checked && style.wrapperChecked,
                  tradename === Tradename.LOOKIERO && style.wrapperLookiero,
                ]}
                accessible
                onPress={() => {
                  onChange(optionValue);
                  onClose();
                }}
              >
                <Checkbox checked={checked} />

                <Text style={style.text}>{label}</Text>
              </Pressable>
            );
          })}
        </View>
      </View>
    </Modal>
  );
};

export type { Option };
export { SelectModal };
